/**
 * Intercom Chat Support Utility
 *
 * Lightweight wrapper around the Intercom JS API.
 * Loads the script on demand and provides boot/show/hide/shutdown helpers.
 */

const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID || 'mi73h8ob'

let scriptLoaded = false
let booted = false

/**
 * Inject the Intercom script tag into the page (idempotent).
 */
export function loadIntercom() {
  if (scriptLoaded || typeof window === 'undefined') return

  // Create the Intercom global stub so calls queue up before the script loads
  window.intercomSettings = { app_id: INTERCOM_APP_ID, hide_default_launcher: true }

  const w = window
  const ic = w.Intercom
  if (typeof ic === 'function') {
    ic('reattach_activator')
    ic('update', w.intercomSettings)
    scriptLoaded = true
    return
  }

  const i = function () {
    i.c(arguments)
  }
  i.q = []
  i.c = function (args) {
    i.q.push(args)
  }
  w.Intercom = i

  const s = document.createElement('script')
  s.type = 'text/javascript'
  s.async = true
  s.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`
  const x = document.getElementsByTagName('script')[0]
  if (x && x.parentNode) {
    x.parentNode.insertBefore(s, x)
  } else {
    document.head.appendChild(s)
  }

  scriptLoaded = true
}

/**
 * Boot Intercom with user identity so conversations are tied to the user.
 * Safe to call multiple times — only boots once.
 */
export function bootIntercom(userData = {}) {
  if (booted) return
  loadIntercom()

  const payload = {
    app_id: INTERCOM_APP_ID,
    hide_default_launcher: true, // start hidden; IntercomManager will show when appropriate
    ...userData,
  }

  if (window.Intercom) {
    window.Intercom('boot', payload)
    booted = true
  }
}

/**
 * Open the Intercom messenger (chat window).
 * Safe to call even if not yet booted — it will boot first.
 */
export function openIntercomChat() {
  if (!booted) loadIntercom()
  if (window.Intercom) {
    // If the user explicitly opens chat (e.g. profile dropdown) on a route
    // where Intercom is CSS-hidden, unhide it so the messenger is visible.
    document.body.classList.remove('cd-intercom-hidden')
    window.Intercom('show')
  }
}

/**
 * Show the default launcher bubble.
 */
export function showLauncher() {
  document.body.classList.remove('cd-intercom-hidden')
  if (!window.Intercom) return
  window.intercomSettings = { ...window.intercomSettings, hide_default_launcher: false }
  window.Intercom('update', { hide_default_launcher: false })
}

/**
 * Hide the launcher bubble and close any open messenger.
 * hide_default_launcher only hides the launcher button — unread in-app
 * message previews still pop up — so also hide Intercom's DOM wholesale
 * via the cd-intercom-hidden body class (see globals.css).
 */
export function hideLauncher() {
  document.body.classList.add('cd-intercom-hidden')
  if (!window.Intercom) return
  window.intercomSettings = { ...window.intercomSettings, hide_default_launcher: true }
  window.Intercom('update', { hide_default_launcher: true })
  window.Intercom('hide')
}

/**
 * Shutdown Intercom completely (e.g. on logout).
 */
export function shutdownIntercom() {
  if (window.Intercom) {
    window.Intercom('shutdown')
    booted = false
  }
}
